import { useState, useCallback } from "react";
import { useToast } from "@/hooks/use-toast";
import { 
  createSharedLink, 
  deactivateSharedLink,
  reactivateSharedLink,
  getRecordSharedLinks,
  copyShareUrlToClipboard,
  generateShareUrl,
  canShareRecord
} from "@/integrations/sharing/sharingService";
import type { SharedLink, CreateSharedLinkParams } from "@/integrations/sharing/sharingService";

export interface UseSharedLinksReturn {
  // State
  isLoading: boolean;
  sharedLinks: SharedLink[];
  
  // Actions
  createShare: (recordId: string, expiresAt?: string | null) => Promise<string | null>;
  deactivateShare: (sharedLinkId: string) => Promise<boolean>;
  reactivateShare: (sharedLinkId: string) => Promise<boolean>;
  copyToClipboard: (token: string) => Promise<boolean>;
  refreshLinks: (recordId: string) => Promise<void>;
  checkCanShare: (recordId: string) => Promise<boolean>;
  getShareUrl: (token: string) => string;
}

export const useSharedLinks = (): UseSharedLinksReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [sharedLinks, setSharedLinks] = useState<SharedLink[]>([]);
  const { toast } = useToast();

  const createShare = useCallback(async (
    recordId: string, 
    expiresAt?: string | null
  ): Promise<string | null> => {
    setIsLoading(true);
    try {
      const result = await createSharedLink({ recordId, expiresAt });
      
      if ('error' in result) {
        toast({
          title: "Ошибка создания ссылки",
          description: result.message,
          variant: "destructive",
        });
        return null;
      }

      toast({
        title: "Ссылка создана",
        description: "Ссылка для шаринга успешно создана",
      });

      // Обновляем список ссылок
      await refreshLinks(recordId);
      
      return result.token;
    } catch (error) {
      console.error('Error creating shared link:', error);
      toast({
        title: "Ошибка",
        description: "Произошла ошибка при создании ссылки",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const deactivateShare = useCallback(async (sharedLinkId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const result = await deactivateSharedLink(sharedLinkId);
      
      if (typeof result === 'object' && 'error' in result) {
        toast({
          title: "Ошибка деактивации",
          description: result.message,
          variant: "destructive",
        });
        return false;
      }

      if (result) {
        toast({
          title: "Ссылка деактивирована",
          description: "Доступ по ссылке отключен",
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error deactivating shared link:', error);
      toast({
        title: "Ошибка",
        description: "Произошла ошибка при деактивации ссылки",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const reactivateShare = useCallback(async (sharedLinkId: string): Promise<boolean> => {
    setIsLoading(true);
    try {
      const result = await reactivateSharedLink(sharedLinkId);
      
      if (typeof result === 'object' && 'error' in result) {
        toast({
          title: "Ошибка реактивации",
          description: result.message,
          variant: "destructive",
        });
        return false;
      }

      if (result) {
        toast({
          title: "Ссылка активирована",
          description: "Доступ по ссылке восстановлен",
        });
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error reactivating shared link:', error);
      toast({
        title: "Ошибка",
        description: "Произошла ошибка при реактивации ссылки",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  const copyToClipboard = useCallback(async (token: string): Promise<boolean> => {
    try {
      const success = await copyShareUrlToClipboard(token);
      
      if (success) {
        toast({
          title: "Ссылка скопирована",
          description: "Ссылка успешно скопирована в буфер обмена",
        });
        return true;
      } else {
        toast({
          title: "Ошибка копирования",
          description: "Не удалось скопировать ссылку",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Error copying to clipboard:', error);
      toast({
        title: "Ошибка",
        description: "Произошла ошибка при копировании ссылки",
        variant: "destructive",
      });
      return false;
    }
  }, [toast]);

  const refreshLinks = useCallback(async (recordId: string): Promise<void> => {
    try {
      const result = await getRecordSharedLinks(recordId);
      
      if (Array.isArray(result)) {
        setSharedLinks(result);
      } else {
        console.error('Error fetching shared links:', result);
        setSharedLinks([]);
      }
    } catch (error) {
      console.error('Error refreshing shared links:', error);
      setSharedLinks([]);
    }
  }, []);

  const checkCanShare = useCallback(async (recordId: string): Promise<boolean> => {
    try {
      const result = await canShareRecord(recordId);
      return typeof result === 'boolean' ? result : false;
    } catch (error) {
      console.error('Error checking record share eligibility:', error);
      return false;
    }
  }, []);

  const getShareUrl = useCallback((token: string): string => {
    return generateShareUrl(token);
  }, []);

  return {
    isLoading,
    sharedLinks,
    createShare,
    deactivateShare,
    reactivateShare,
    copyToClipboard,
    refreshLinks,
    checkCanShare,
    getShareUrl,
  };
};